/**
 * Multi-clip layout presets.
 *
 * A preset is nothing but a list of slot rectangles in normalized 0..1 frame
 * space. The same data drives the panel thumbnails and the transforms written
 * onto clips, so a preset thumbnail cannot disagree with what applying it does.
 *
 * RENDERER CONVENTION (verified against packages/core/src/video/video-engine.ts,
 * drawFrameToContext around line 1333 and the identical export path around
 * line 3385):
 *
 *   ctx.translate(canvasWidth / 2 + transform.position.x,
 *                 canvasHeight / 2 + transform.position.y)
 *   ctx.rotate(transform.rotation)
 *   ctx.scale(transform.scale.x, transform.scale.y)
 *   drawWidth/drawHeight = the source frame fitted to the FULL CANVAS per
 *     transform.fitMode ("contain" when unset or "none")
 *   ctx.drawImage(frame, -drawWidth * anchor.x, -drawHeight * anchor.y, ...)
 *
 * So for VIDEO clips:
 *   - position is a PIXEL OFFSET FROM FRAME CENTRE, not a normalized 0..1
 *     coordinate. Text clips elsewhere in this codebase use normalized
 *     coordinates; video clips do not. Do not mix the two.
 *   - scale 1 means "fitted to the whole canvas", not "source pixel size". A
 *     slot that is half the frame therefore needs roughly scale 0.5, not a
 *     scale derived from the source resolution.
 *   - anchor is a fraction of the DRAWN size subtracted from the origin, so
 *     anchor 0.5/0.5 is what centres the image on the translated origin. The
 *     placement maths below assumes that anchor and sets it explicitly.
 *
 * Two honest limitations of that renderer, neither of which this module can fix:
 *   - There is no per-slot clip region. A "cover" clip is drawn larger than its
 *     slot whenever the source aspect does not match the slot aspect, and it
 *     bleeds over neighbouring slots instead of being cropped to its own.
 *   - When transform.crop is set the renderer ignores fitMode and contain-fits
 *     the cropped rectangle instead, so a cropped clip lands inside its slot
 *     rather than covering it.
 */

/** One slot in a layout, in normalized 0..1 frame space. */
export interface LayoutSlot {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
}

/** A named arrangement of slots. */
export interface LayoutPreset {
  id: string;
  name: string;
  slots: LayoutSlot[];
}

/** Project canvas size in pixels, from project.settings.width/height. */
export interface LayoutFrameSize {
  width: number;
  height: number;
}

/** The transform fields a layout writes onto a clip. */
export interface LayoutPlacement {
  position: { x: number; y: number };
  scale: { x: number; y: number };
  anchor: { x: number; y: number };
  fitMode: "cover";
  /**
   * The slot, handed to the renderer as a clip region. "cover" overdraws past
   * the slot edges whenever the clip's aspect differs from the slot's, and
   * without this the overflow would paint over neighbouring slots.
   */
  layoutSlot: { x: number; y: number; width: number; height: number };
}

/** A slot resolved to pixels, used by the placement maths and by tests. */
export interface LayoutPixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const THIRD = 1 / 3;
const TWO_THIRDS = 2 / 3;
const QUARTER = 0.25;

/** Inset size and margin for picture in picture slots, as a fraction of frame. */
const PIP_SIZE = 0.28;
const PIP_MARGIN = 0.04;
const PIP_FAR = 1 - PIP_MARGIN - PIP_SIZE;

const slot = (
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
): LayoutSlot => ({ x, y, w, h, label });

/**
 * Slot order is assignment order: slot 0 takes the first clip. Hero slots come
 * first so the leading clip becomes the hero rather than a corner thumbnail.
 */
export const LAYOUT_PRESETS: LayoutPreset[] = [
  {
    id: "side-by-side",
    name: "Side by Side",
    slots: [slot(0, 0, 0.5, 1, "L"), slot(0.5, 0, 0.5, 1, "R")],
  },
  {
    id: "hero-left",
    name: "Hero on Left",
    slots: [
      slot(0, 0, TWO_THIRDS, 1, "H"),
      slot(TWO_THIRDS, 0, THIRD, 0.5, "TR"),
      slot(TWO_THIRDS, 0.5, THIRD, 0.5, "BR"),
    ],
  },
  {
    id: "hero-right",
    name: "Hero on Right",
    slots: [
      slot(THIRD, 0, TWO_THIRDS, 1, "H"),
      slot(0, 0, THIRD, 0.5, "TL"),
      slot(0, 0.5, THIRD, 0.5, "BL"),
    ],
  },
  {
    id: "three-columns",
    name: "3 Columns",
    slots: [
      slot(0, 0, THIRD, 1, "1"),
      slot(THIRD, 0, THIRD, 1, "2"),
      slot(TWO_THIRDS, 0, THIRD, 1, "3"),
    ],
  },
  {
    id: "pip-bottom-right",
    name: "PIP Bottom Right",
    slots: [
      slot(0, 0, 1, 1, "B"),
      slot(PIP_FAR, PIP_FAR, PIP_SIZE, PIP_SIZE, "I"),
    ],
  },
  {
    id: "pip-top-right",
    name: "PIP Top Right",
    slots: [
      slot(0, 0, 1, 1, "B"),
      slot(PIP_FAR, PIP_MARGIN, PIP_SIZE, PIP_SIZE, "I"),
    ],
  },
  {
    id: "four-columns",
    name: "4 Columns",
    slots: [
      slot(0, 0, QUARTER, 1, "1"),
      slot(QUARTER, 0, QUARTER, 1, "2"),
      slot(0.5, 0, QUARTER, 1, "3"),
      slot(0.75, 0, QUARTER, 1, "4"),
    ],
  },
  {
    id: "grid-2x2",
    name: "2x2 Grid",
    slots: [
      slot(0, 0, 0.5, 0.5, "TL"),
      slot(0.5, 0, 0.5, 0.5, "TR"),
      slot(0, 0.5, 0.5, 0.5, "BL"),
      slot(0.5, 0.5, 0.5, 0.5, "BR"),
    ],
  },
  {
    id: "top-and-bottom",
    name: "Top and Bottom",
    slots: [slot(0, 0, 1, 0.5, "T"), slot(0, 0.5, 1, 0.5, "B")],
  },
  {
    id: "three-rows",
    name: "3 Rows",
    slots: [
      slot(0, 0, 1, THIRD, "1"),
      slot(0, THIRD, 1, THIRD, "2"),
      slot(0, TWO_THIRDS, 1, THIRD, "3"),
    ],
  },
  {
    id: "four-rows",
    name: "4 Rows",
    slots: [
      slot(0, 0, 1, QUARTER, "1"),
      slot(0, QUARTER, 1, QUARTER, "2"),
      slot(0, 0.5, 1, QUARTER, "3"),
      slot(0, 0.75, 1, QUARTER, "4"),
    ],
  },
  {
    id: "pip-bottom-left",
    name: "PIP Bottom Left",
    slots: [
      slot(0, 0, 1, 1, "B"),
      slot(PIP_MARGIN, PIP_FAR, PIP_SIZE, PIP_SIZE, "I"),
    ],
  },
  {
    id: "pip-top-left",
    name: "PIP Top Left",
    slots: [
      slot(0, 0, 1, 1, "B"),
      slot(PIP_MARGIN, PIP_MARGIN, PIP_SIZE, PIP_SIZE, "I"),
    ],
  },
  {
    id: "hero-top",
    name: "Hero on Top",
    slots: [
      slot(0, 0, 1, TWO_THIRDS, "H"),
      slot(0, TWO_THIRDS, 0.5, THIRD, "BL"),
      slot(0.5, TWO_THIRDS, 0.5, THIRD, "BR"),
    ],
  },
  {
    id: "hero-bottom",
    name: "Hero on Bottom",
    slots: [
      slot(0, THIRD, 1, TWO_THIRDS, "H"),
      slot(0, 0, 0.5, THIRD, "TL"),
      slot(0.5, 0, 0.5, THIRD, "TR"),
    ],
  },
];

/**
 * Presets whose slots overlap on purpose: the inset sits on top of the full
 * frame base. Every other preset must tile without overlap.
 */
export const OVERLAPPING_LAYOUT_PRESET_IDS: readonly string[] = [
  "pip-bottom-right",
  "pip-top-right",
  "pip-bottom-left",
  "pip-top-left",
];

export function getLayoutPreset(id: string): LayoutPreset | undefined {
  return LAYOUT_PRESETS.find((preset) => preset.id === id);
}

export function isOverlappingLayoutPreset(id: string): boolean {
  return OVERLAPPING_LAYOUT_PRESET_IDS.includes(id);
}

/** Resolve a normalized slot to a pixel rectangle on the project canvas. */
export function layoutSlotPixelRect(
  target: LayoutSlot,
  frame: LayoutFrameSize,
): LayoutPixelRect {
  return {
    x: target.x * frame.width,
    y: target.y * frame.height,
    width: target.w * frame.width,
    height: target.h * frame.height,
  };
}

const isPositiveFinite = (value: number): boolean =>
  Number.isFinite(value) && value > 0;

/**
 * The size the renderer draws a source at before transform.scale is applied,
 * for fitMode "cover": the source aspect fitted to cover the whole canvas.
 */
function coverBaseSize(
  frame: LayoutFrameSize,
  sourceAspect: number,
): { width: number; height: number } {
  const canvasAspect = frame.width / frame.height;
  if (sourceAspect > canvasAspect) {
    return { width: frame.height * sourceAspect, height: frame.height };
  }
  return { width: frame.width, height: frame.width / sourceAspect };
}

/**
 * Convert a normalized slot into the transform fields the video renderer
 * expects. See the RENDERER CONVENTION note at the top of this file.
 *
 * position: the slot centre expressed as a pixel offset from the frame centre.
 * scale:    uniform on both axes, so a clip is never distorted. When the source
 *           aspect is known the scale is the smallest one that still covers the
 *           slot. When it is unknown, max(w, h) is used, which always covers
 *           the slot because the unscaled cover rect already covers the whole
 *           canvas, at the cost of overdrawing more than strictly necessary.
 */
export function layoutSlotToTransform(
  target: LayoutSlot,
  frame: LayoutFrameSize,
  sourceAspect?: number,
): LayoutPlacement {
  const validFrame =
    isPositiveFinite(frame.width) && isPositiveFinite(frame.height);

  const position = validFrame
    ? {
        x: (target.x + target.w / 2 - 0.5) * frame.width,
        y: (target.y + target.h / 2 - 0.5) * frame.height,
      }
    : { x: 0, y: 0 };

  let uniformScale = Math.max(target.w, target.h);

  if (validFrame && sourceAspect !== undefined && isPositiveFinite(sourceAspect)) {
    const base = coverBaseSize(frame, sourceAspect);
    uniformScale = Math.max(
      (target.w * frame.width) / base.width,
      (target.h * frame.height) / base.height,
    );
  }

  return {
    position,
    scale: { x: uniformScale, y: uniformScale },
    anchor: { x: 0.5, y: 0.5 },
    fitMode: "cover",
    layoutSlot: {
      x: target.x,
      y: target.y,
      width: target.w,
      height: target.h,
    },
  };
}

/**
 * Dynamic Zoom ramps a clip from its current scale to that scale times this
 * multiplier across the clip duration.
 */
export const DYNAMIC_ZOOM_END_MULTIPLIER = 1.12;
